import { useCallback, useEffect, useMemo } from 'react';
import { selectUserEmail } from 'pages/Hospitality/redux';
import { selectHotelData } from 'pages/redux';
import { selectUsername } from 'redux/global';
import { useAppSelector } from 'redux/hooks';
import {
  DbCacheContext,
  DbCacheTable,
  FetchDbDataParams,
  buildStoreName,
  deleteDbCache,
  fetchDbDataWithCache,
  getDbCache,
  isDbCacheValid,
  patchDbCache,
  purgeHotelDbCache,
  purgeOtherHotels,
  setDbCache,
  syncInfoDerivedCaches,
} from 'utils/dbIndexedCache';
import { syncAppDbCache } from 'utils/syncAppDbCache';
import { IResponse } from 'types';

export const useDbCache = () => {
  const hotelData = useAppSelector(selectHotelData);
  const userEmail = useAppSelector(selectUserEmail);
  const username = useAppSelector(selectUsername);

  const ctx = useMemo<DbCacheContext>(() => ({
    hotelId: hotelData?.id != null ? String(hotelData.id) : '',
    userEmail: userEmail || username || undefined,
  }), [hotelData?.id, userEmail, username]);

  useEffect(() => {
    if (!ctx.hotelId) return;
    purgeOtherHotels(ctx.hotelId);
  }, [ctx.hotelId]);

  const fetchDbData = useCallback(<T = unknown>(
    params: Omit<FetchDbDataParams<T>, 'fetchFromBe'> & {
      fetchFromBe: () => Promise<IResponse>;
    },
  ) => fetchDbDataWithCache<T>(ctx, params), [ctx]);

  const updateDbCache = useCallback(async (
    table: DbCacheTable,
    data: unknown,
    cacheKey?: string,
  ) => {
    if (!ctx.hotelId) return;
    const storeName = buildStoreName(ctx.hotelId, table, ctx.userEmail, cacheKey);
    if (table === 'info' && data && typeof data === 'object') {
      await syncInfoDerivedCaches(ctx, data as Record<string, unknown>);
      return;
    }
    await setDbCache(storeName, data);
  }, [ctx]);

  const patchCache = useCallback(async (
    table: DbCacheTable,
    updater: (current: unknown | null) => unknown,
    cacheKey?: string,
  ) => {
    if (!ctx.hotelId) return;
    const storeName = buildStoreName(ctx.hotelId, table, ctx.userEmail, cacheKey);
    await patchDbCache(storeName, updater);
  }, [ctx]);

  const invalidateDbCache = useCallback(async (
    table: DbCacheTable,
    cacheKey?: string,
  ) => {
    if (!ctx.hotelId) return;
    const storeName = buildStoreName(ctx.hotelId, table, ctx.userEmail, cacheKey);
    await deleteDbCache(storeName);
  }, [ctx]);

  const readDbCache = useCallback(async <T = unknown>(
    table: DbCacheTable,
    cacheKey?: string,
  ) => {
    if (!ctx.hotelId) return null;
    const storeName = buildStoreName(ctx.hotelId, table, ctx.userEmail, cacheKey);
    const cached = await getDbCache<T>(storeName);
    if (!cached || !isDbCacheValid(cached.fetchedAt)) return null;
    return cached.data;
  }, [ctx]);

  return {
    ctx,
    fetchDbData,
    updateDbCache,
    patchCache,
    invalidateDbCache,
    readDbCache,
    purgeOtherHotels: () => (ctx.hotelId ? purgeOtherHotels(ctx.hotelId) : Promise.resolve()),
    purgeHotelDbCache: () => (ctx.hotelId ? purgeHotelDbCache(ctx.hotelId) : Promise.resolve()),
    syncAppDbCache,
  };
};

export { fetchDbDataWithCache, getDbCacheContextFromState } from 'utils/dbIndexedCache';
export { syncAppDbCache } from 'utils/syncAppDbCache';
